import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useRefetchableFragment, useRelayEnvironment } from 'react-relay';

import DashboardControl from 'src/components/Dashboard/DashboardControl';
import { WARNING_TIMEOUT, showTradeRuleMsg } from 'src/components/Dashboard/DashboardControl/helpers/common';
import LoadingOverlay from 'src/components/Dashboard/LoadingOverlay/index';
import Placeholder from 'src/components/Dashboard/Placeholder';
import {
  buildChartViewKey,
  buildEntityShowState,
  buildStateURLParams,
  navigateToEntityShowState,
} from 'src/components/Dashboard/helpers/common';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import Loading from 'src/components/Loading';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import Map from 'src/enosikit/components/Map';
import FlashesStore from 'src/stores/FlashesStore';
import getFullAddressString from 'src/util/address';
import {
  DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY,
  DATA_GROUP_BY_TRADE_TYPE,
  DEFAULT_MAX_VISIBLE_CARDS_COUNT,
  SOURCE, SOURCE_TRADES, TIME_ZONE_SYSTEM,
  UNIT, UNIT_CURRENCY, UNIT_ENERGY, VIEW_SELECTOR,
  X_AXIS_DOMAIN_FIXED,
} from 'src/util/constants';
import BrowserProtocol from 'src/util/history';
import formatAsyncErrorBodyText from 'src/util/i18n/formatAsyncErrorBodyText';
import { getDefaultLocale } from 'src/util/i18n/handler';
import logger from 'src/util/logger';
import {
  dateRangeToTimeRange,
  getAggregation,
  getDashboardTimeScope,
  timeInZoneAtStartOfDay,
  timespanChanged,
} from 'src/util/time';

import PropertyShowTradeEnergy from './PropertyShowTradeEnergy';
import { buildMainData } from './data/mainDataBuilder';
import buildLoadingOverlayState, { CHECKLIST_STATES, STATE_METADATA } from './helpers/buildLoadingOverlayState';
import { messages } from './helpers/constants';
import { getCache, lookupCache } from './helpers/dataCaching';
import { chartViewSelector, downloadManager } from './helpers/propertyShow';
import usePropertyData from './hooks/usePropertyData';
import {
  PropertyShowLoadingActions,
  PropertyShowLoadingStates,
  defaultInitialState,
} from './reducer/constants';
import loadingStateReducer from './reducer/propertyShowLoadingReducer';

// Static property data
const PropertyShowQuery = graphql`
fragment PropertyShow on Query
@throwOnFieldError
@refetchable(queryName: "PropertyShowRefetchQuery")
@argumentDefinitions(
  uuid: {type: "ID!"}
) {
  property(uuid:$uuid) {
    uuid
    externalIdentifier
    active { start finish }
    title
    timezone
    publicHolidayRegion
    address {
      line1
      line2
      city
      state
      country
      postcode
    }
  }
}
`;

/**
 * Property dashboard page. Renders chart controls, energy data, trade data, and error
 * states for a single property. Accepts the found-relay route props merged with the
 * `PropertyShow` Relay fragment reference (`graphql/syndesi.graphql`).
 * @param {object} props - Component props including the `PropertyShow` fragment reference.
 * @param {object} props.match - Found-relay route match containing `params.uuid` and `location`.
 * @param {object|null} [props.datespanFinish] - Layout-injected current date range finish.
 * @param {object|null} [props.datespanStart] - Layout-injected current date range start.
 * @param {object} [props.initialView] - Initial chart view (aggregateBy, groupBy, xAxisDomain)
 *   derived from route query params.
 * @param {Function} [props.onDatespanChangeFunc] - Layout-injected date change handler.
 * @param {string} [props.queryTimezone] - Optional timezone override from the URL query string.
 * @returns {React.ReactNode} property dashboard page content.
 */
function PropertyShow({
  queryTimezone = undefined,
  initialView = undefined,
  onDatespanChangeFunc = undefined,
  ...props
}) {
  if (!props) {
    return <Loading />;
  }

  const { match } = props;
  const [data] = useRefetchableFragment(PropertyShowQuery, props);
  const { property } = data || {};
  const propertyTimezone = property?.timezone || APIConfig().DEFAULT_TIMEZONE || TIME_ZONE_SYSTEM;
  const timezone = queryTimezone || propertyTimezone;

  const searchParams = new URLSearchParams(window.location.search);

  // Parse URL parameters and convert ISO strings to Unix timestamps for getDashboardTimeScope
  const parsedStart = DateTime.fromISO(searchParams.get('start'), { zone: timezone });
  const parsedFinish = DateTime.fromISO(searchParams.get('finish'), { zone: timezone });
  const start = parsedStart.isValid ? parsedStart.startOf('day').toSeconds() : null;
  const finish = parsedFinish.isValid ? parsedFinish.plus({ days: 1 }).startOf('day').toSeconds() : null;
  const { s, f } = getDashboardTimeScope(start, finish, timezone);
  const intl = useIntl();
  const [datespan, setDatespan] = useState({
    start: s,
    finish: f,
  });

  const [unit, setUnit] = useState(UNIT_ENERGY);
  const [source, setSource] = useState(SOURCE_TRADES);
  const [tradeBtnClicks, setTradeBtnClicks] = useState(0);
  const [viewSelectorOpen, setViewSelectorOpen] = useState(false);
  const [downloadSelectorOpen, setDownloadSelectorOpen] = useState(false);
  const [chartView, setChartView] = useState(initialView);

  // Get Relay environment for lazy loading propertyUsers
  const relayEnvironment = useRelayEnvironment();

  // Loading state reducer for centralized state management
  const [loadingReducerState, dispatchLoadingAction] = useReducer(
    loadingStateReducer,
    defaultInitialState(),
  );
  const { aggregateBy, groupBy, xAxisDomain } = chartView || {};

  const cardSelectedKeysParam = searchParams.get('cardSelectedKeys');
  const selectedKeys = React.useMemo(() => {
    if (!cardSelectedKeysParam) {
      return {};
    }

    const chartViewKey = buildChartViewKey(chartView, source);
    if (!chartViewKey) {
      return {};
    }

    const keysArray = cardSelectedKeysParam.split(',').filter((key) => key.trim() !== '');
    return keysArray.length > 0 ? { [chartViewKey]: keysArray } : {};
  }, [cardSelectedKeysParam, chartView, source]);

  const firstTimeRender = useRef(true);
  // Tracks whether the grouping message should be shown.
  // Once true, stays true until overlay closes.
  const showGroupingMessageRef = useRef(false);

  // Captures the pathname this component instance was mounted for. Initialized
  // once from match.location.pathname and never updated. Used by updateURLState
  // to abort any URL write once the live pathname has diverged from the mount
  // route (i.e. the user has navigated away). The key={uuid} on the route config
  // guarantees a fresh instance — and therefore a fresh ref — for every distinct
  // property UUID.
  const mountedPathnameRef = useRef(props?.match?.location?.pathname);

  const {
    finalDatespan,
    finalHistorianAggregation,
    finalTradeAggregation,
  } = useMemo(() => {
    const { start: updatedStart, finish: updatedFinish } = dateRangeToTimeRange(datespan, timezone);
    const aggregate = getAggregation(updatedStart, updatedFinish);

    return {
      finalDatespan: {
        start: updatedStart,
        finish: updatedFinish,
      },
      finalHistorianAggregation: aggregate,
      finalTradeAggregation: aggregate,
    };
  }, [datespan, timezone]);

  // Snapshot of the last successfully loaded `{ start, finish, aggregation }`.
  // Written only by the success-snapshot effect below (never on picker change),
  // so it always corresponds to a segment present in the module-level cache and
  // can be used by `handleCancel` / `handleErrorDismiss` to restore prior data
  // without recomputing aggregation from the (possibly failed) current datespan.
  const previousDatespanRef = useRef(null);

  // True while `handleErrorDismiss` is restoring cached data after a fetch error.
  // Suppresses the CANCELLED-state `noDataMessage` during the single render that
  // sits between the dismiss handler's batched updates (Commit A) and the
  // second usePropertyData effect re-hydrating combinedData (Commit B).
  const [isRecoveringFromCache, setIsRecoveringFromCache] = useState(false);

  const {
    combinedData, cancelFunc, clearError, error, handleRetry, isRetrying, queryType,
  } = usePropertyData({
    propertyId: property?.uuid,
    dateSpan: finalDatespan,
    timezone,
    initialData: data,
    historianAggregation: finalHistorianAggregation,
    tradeAggregation: finalTradeAggregation,
    stateDispatchFunc: dispatchLoadingAction,
  });

  const mainData = useMemo(
    () => buildMainData(combinedData?.property || property, datespan),
    [combinedData?.property, property, datespan],
  );

  // Track whether the chart has finished rendering (used to coordinate overlay hiding)
  const isChartReadyRef = useRef(false);

  // Ref to track the onChartReady timeout for cleanup
  const chartReadyTimeoutRef = useRef(null);

  // Derive overlay visibility from loading state.
  // Overlay is visible during loading, RENDERING, and COMPLETE states.
  // IDLE and ERROR are terminal states where overlay is hidden.
  const {
    IDLE, COMPLETE, ERROR, CANCELLED,
  } = PropertyShowLoadingStates;
  const overlayVisible = loadingReducerState.state !== IDLE
    && loadingReducerState.state !== ERROR
    && loadingReducerState.state !== CANCELLED;

  // On initial page load, apply default chartView settings based on rules count.
  // This runs once when loading completes (overlay becomes hidden) and mainData is populated.
  // We use meters data (hasMetersData) to determine if mainData has been populated.
  const rulesCount = Object.keys(mainData.buy.rules).length
    + Object.keys(mainData.sell.rules).length;
  const hasMetersData = Object.keys(mainData.meters).length > 0;
  const loadingCompletedSuccessfully = loadingReducerState.state === COMPLETE
    || loadingReducerState.state === IDLE;
  useEffect(() => {
    // Skip unless loading completed successfully and mainData is populated.
    // This prevents applying defaults after CANCELLED or ERROR states,
    // where meters data may exist from a prior successful load.
    if (!loadingCompletedSuccessfully || !hasMetersData) {
      return;
    }

    // Per-field idempotency guard: skip when all three defaults are already set
    // (e.g. loaded from URL params or applied on a previous render). Each field
    // is applied independently so a partial URL (e.g. only groupBy present) still
    // gets its missing siblings backfilled.
    if (groupBy && aggregateBy && xAxisDomain) {
      return;
    }

    // The maximum possible number of cards from 16 rules is 16 cards. This maximum of 16 cards
    // results in a 4x4 grid layout (4 rows of 4 cards on a normal laptop screen).
    // Beyond 16 rules, we switch to grouping by trade type to avoid potentially too many cards.
    const defaultGroupBy = rulesCount > DEFAULT_MAX_VISIBLE_CARDS_COUNT
      ? DATA_GROUP_BY_TRADE_TYPE
      : DATA_GROUP_BY_COUNTERPARTY;

    setChartView((prevView) => ({
      ...prevView,
      groupBy: prevView.groupBy || defaultGroupBy,
      aggregateBy: prevView.aggregateBy || DATA_AGGREGATE_BY_PROPERTY,
      xAxisDomain: prevView.xAxisDomain || X_AXIS_DOMAIN_FIXED,
    }));
  }, [loadingCompletedSuccessfully, hasMetersData, rulesCount, groupBy, aggregateBy, xAxisDomain]);
  // Update the context whenever datespan changes
  useEffect(() => {
    if (onDatespanChangeFunc && datespan?.start) {
      onDatespanChangeFunc(datespan.start, datespan.finish);
    }
  }, [datespan?.start, datespan?.finish, onDatespanChangeFunc]);

  /**
   * Synchronizes current chart view state (date range, timezone, view settings) to browser URL
   * query parameters using REPLACE navigation to avoid creating new history entries.
   */
  const updateURLState = useCallback(() => {
    if (window.location.pathname !== mountedPathnameRef.current) {
      logger.log('Pathname diverged from mount pathname, skipping URL update to avoid overwriting navigation');
      return;
    }

    const urlState = buildEntityShowState(
      datespan,
      match,
      chartView,
      timezone,
      source,
      unit,
      selectedKeys,
    );

    if (!urlState) {
      return;
    }

    navigateToEntityShowState(urlState);
  }, [datespan, match, chartView, timezone, source, unit, selectedKeys]);

  const doRefetch = () => {
    // Reset chart ready state for new data load
    isChartReadyRef.current = false;

    const { start: updatedStart, finish: updatedFinish } = dateRangeToTimeRange(
      datespan,
      timezone,
    );

    const { location } = match;
    const { pathname } = location;

    const state = buildStateURLParams(
      chartView,
      updatedStart,
      updatedFinish.minus({ days: 1 }),
      timezone,
      source,
      unit,
      selectedKeys,
    );

    if (!state) {
      logger.warn('Failed to generate URL state, skipping update');
      return;
    }

    const searchString = Object.keys(state)
      .map((k) => [k, encodeURIComponent(state[k])].join('='))
      .join('&');
    const formattedSearchString = `?${searchString}`;

    BrowserProtocol.navigate({
      action: 'REPLACE',
      pathname,
      search: formattedSearchString,
      hash: '',
      state,
    });
  };

  // Temporary code until we implement updating the overlay message for each batch
  // of meters and trade rules.
  // See the commented-out useEffect below for the intended implementation.
  // See comment in dataBatching.js for more details.
  /**
   * Manages timed state transitions for the loading overlay.
   * Each loading stage displays its completion message for a minimum duration
   * before transitioning to the next stage, ensuring users have time to see
   * progress feedback.
   * - METERS_LOADED: show "Loaded X meters"
   * - RULES_LOADED: show "Loaded X trade rules"
   * - PREPARING_DATA: show "Preparing data"
   * - RENDERING: show "Rendering data"
   * - COMPLETE: show "Data rendered"
   */
  useEffect(() => {
    let timerId;

    if (loadingReducerState.state === PropertyShowLoadingStates.METERS_LOADED) {
      dispatchLoadingAction({ type: PropertyShowLoadingActions.METERS_DISPLAYED });
    } else if (loadingReducerState.state === PropertyShowLoadingStates.RULES_LOADED) {
      dispatchLoadingAction({ type: PropertyShowLoadingActions.RULES_DISPLAYED });
    } else if (loadingReducerState.state === PropertyShowLoadingStates.PREPARING_DATA) {
      dispatchLoadingAction({ type: PropertyShowLoadingActions.DATA_PREPARED });
    } else if (loadingReducerState.state === PropertyShowLoadingStates.RENDERING) {
      // Transition to COMPLETE when:
      // - Chart has finished rendering (isChartReadyRef), OR
      // - groupBy is not set yet (no chart to wait for), OR
      // - No meters data (chart won't render, so onChartReady will never fire)
      if (isChartReadyRef.current || !groupBy || !hasMetersData) {
        dispatchLoadingAction({ type: PropertyShowLoadingActions.RENDERING_DISPLAYED });
      }
    } else if (loadingReducerState.state === PropertyShowLoadingStates.COMPLETE) {
      timerId = setTimeout(() => {
        dispatchLoadingAction({ type: PropertyShowLoadingActions.HIDE_OVERLAY });
      }, 1000);
    }

    return () => clearTimeout(timerId);
  }, [loadingReducerState.state, groupBy, hasMetersData]);

  /** Callback invoked when the chart has completed its initial render. */
  const onChartReady = useCallback(() => {
    isChartReadyRef.current = true;
    // Wait 1 second after chart is ready to let users read Stage 8 message,
    // then transition to COMPLETE
    if (loadingReducerState.state === PropertyShowLoadingStates.RENDERING) {
      // Clear any existing timeout to prevent duplicate dispatches
      if (chartReadyTimeoutRef.current) {
        clearTimeout(chartReadyTimeoutRef.current);
      }
      chartReadyTimeoutRef.current = setTimeout(() => {
        dispatchLoadingAction({ type: PropertyShowLoadingActions.RENDERING_DISPLAYED });
        chartReadyTimeoutRef.current = null;
      }, 1000);
    }
  }, [loadingReducerState.state]);

  // Cleanup the onChartReady timeout on unmount
  useEffect(() => () => {
    if (chartReadyTimeoutRef.current) {
      clearTimeout(chartReadyTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!firstTimeRender.current) {
      doRefetch();
    }
  }, [datespan]);

  useEffect(() => {
    // firstTimeRender guards the initial mount so the URL is only written once a
    // chart-view field actually changes (e.g. when the defaults effect commits).
    if (firstTimeRender.current) {
      return;
    }

    updateURLState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregateBy, groupBy, xAxisDomain, source, unit]);

  useEffect(() => {
    firstTimeRender.current = false;
  }, []);

  // Manage showGroupingMessageRef based on loading state and groupBy
  useEffect(() => {
    const currentStateOrder = STATE_METADATA[loadingReducerState.state]?.completedUpTo ?? 0;

    // Start showing the message when we reach RULES stage and groupBy is not set from URL
    const reachedRulesStage = currentStateOrder >= CHECKLIST_STATES.RULES;
    if (reachedRulesStage && !groupBy && !showGroupingMessageRef.current) {
      showGroupingMessageRef.current = true;
    }

    // Reset the ref when overlay closes (state becomes IDLE or CANCELLED)
    if (loadingReducerState.state === IDLE || loadingReducerState.state === CANCELLED) {
      showGroupingMessageRef.current = false;
    }
  }, [loadingReducerState.state, groupBy]);

  /** Handles cancellation of in-flight data requests. */
  const handleCancel = useCallback(() => {
    cancelFunc();
    dispatchLoadingAction({ type: PropertyShowLoadingActions.CANCEL_REQUEST });

    if (combinedData && previousDatespanRef.current?.start && previousDatespanRef.current?.finish) {
      // We have prior data to display. The defaults effect has already committed
      // groupBy/aggregateBy/xAxisDomain on the previous successful load, so no
      // manual setChartView is needed here. Revert to the previous datespan so
      // the cached data is re-displayed.
      setDatespan({
        start: previousDatespanRef.current.start,
        finish: previousDatespanRef.current.finish,
      });
      FlashesStore.flash(
        FlashesStore.SUCCESS,
        intl.formatMessage({
          id: 'property.property_show.cancel.reverted.message',
          defaultMessage: 'Request cancelled. Last available data has been restored.',
        }),
      );
    } else {
      // No prior data to display. Leave chartView/groupBy untouched so the URL
      // stays clean; the correct default groupBy is applied on the next
      // successful load based on the actual rules count.
      FlashesStore.flash(
        FlashesStore.SUCCESS,
        intl.formatMessage({
          id: 'property.property_show.cancel.no_data.message',
          defaultMessage: 'Request has been successfully cancelled.',
        }),
      );
    }
  }, [cancelFunc, combinedData, intl]);

  /**
   * Handles dismissal of the fetch-error modal. When the module-level cache
   * still holds data for `previousDatespanRef.current` at the current
   * aggregation, clears the error, reverts `datespan`, and returns true so any
   * route boundary can reset only after the local error source has been cleared.
   * On a cache miss the error state is preserved and false is returned, so the
   * fallback can dismiss the modal while keeping the placeholder mounted.
   */
  const handleErrorDismiss = useCallback(() => {
    const previous = previousDatespanRef.current;
    if (previous?.start && previous?.finish && previous?.aggregation) {
      const previousTimeRange = dateRangeToTimeRange(
        { start: previous.start, finish: previous.finish },
        timezone,
      );
      const cachedSegment = lookupCache(
        getCache(),
        previousTimeRange,
        previous.aggregation,
      );
      if (cachedSegment) {
        // Mark recovery in-flight so `noDataMessage` is suppressed during the
        // render between this handler's batched updates and the second
        // usePropertyData effect re-hydrating combinedData. The flag is reset
        // by the effect below once combinedData populates or the reducer
        // settles into a terminal state.
        setIsRecoveringFromCache(true);
        clearError();
        setDatespan({ start: previous.start, finish: previous.finish });
        FlashesStore.flash(
          FlashesStore.SUCCESS,
          intl.formatMessage({
            id: 'property.property_show.cache_hit_warning.message',
            defaultMessage: 'Request cancelled. Last available data has been restored.',
          }),
        );

        return true;
      }
    }

    return false;
  }, [clearError, intl, timezone]);

  // Reset the recovery flag once the cache hit has been applied (combinedData
  // populated) or the reducer reaches a terminal state. Keeping it true past
  // that point would silently suppress legitimate cancel-with-no-data messages.
  useEffect(() => {
    if (!isRecoveringFromCache) return;
    if (combinedData
      || loadingReducerState.state === IDLE
      || loadingReducerState.state === ERROR) {
      setIsRecoveringFromCache(false);
    }
  }, [isRecoveringFromCache, combinedData, loadingReducerState.state, IDLE, ERROR]);

  // Snapshot the current `{ start, finish, aggregation }` into
  // `previousDatespanRef` only when the pipeline reaches COMPLETE with valid
  // data. Because the same path that flips state to COMPLETE is the path that
  // calls `addSegmentToCache`, the snapshot is guaranteed to correspond to a
  // cached segment — so the lookup in `handleErrorDismiss` cannot miss for
  // invariant reasons. Failed fetches (which never reach COMPLETE) leave the
  // ref pointing at the last good range, preserving the recovery chain across
  // consecutive errors.
  useEffect(() => {
    if (loadingReducerState.state === COMPLETE && combinedData) {
      previousDatespanRef.current = {
        start: datespan.start,
        finish: datespan.finish,
        aggregation: finalHistorianAggregation,
      };
    }
  }, [
    loadingReducerState.state,
    combinedData,
    datespan.start,
    datespan.finish,
    finalHistorianAggregation,
    COMPLETE,
  ]);

  /** Toggles the view or download selector dropdown. */
  const toggle = useCallback((dropdownType) => {
    if (dropdownType === VIEW_SELECTOR) {
      setViewSelectorOpen((prev) => !prev);
    } else {
      setDownloadSelectorOpen((prev) => !prev);
    }
  }, []);

  const controlSetStateFunc = useCallback((opts) => {
    const { group, value } = opts;

    let chartSource = source;
    let chartUnit = unit;
    let btnClicks = tradeBtnClicks;

    FlashesStore.reset();

    if (value === chartSource && chartSource === SOURCE_TRADES) {
      if (btnClicks === 0) {
        clearTimeout();
        btnClicks += 1;
      } else {
        showTradeRuleMsg(intl);
      }
      setTimeout(() => {
        setTradeBtnClicks(0);
      }, WARNING_TIMEOUT);
    }

    if (group === UNIT) {
      chartUnit = value;
    }

    if (group === SOURCE) {
      chartSource = value;
      if (chartUnit === UNIT_CURRENCY) {
        chartUnit = UNIT_ENERGY;
      }
    }

    setSource(chartSource);
    setUnit(chartUnit);
    setTradeBtnClicks(btnClicks);
  }, [source, unit, tradeBtnClicks, intl]);

  /** Handles timespan updates from the date picker. */
  const handleTimespanUpdate = useCallback((updatedStart, updatedFinish) => {
    const inputStart = timeInZoneAtStartOfDay(updatedStart, timezone);
    const inputFinish = timeInZoneAtStartOfDay(updatedFinish, timezone);
    const currentTimespan = {
      start: timeInZoneAtStartOfDay(datespan.start, timezone),
      finish: timeInZoneAtStartOfDay(datespan.finish, timezone),
    };

    const isTimespanChanged = timespanChanged(inputStart, inputFinish, currentTimespan);
    if (!isTimespanChanged && combinedData) {
      return;
    }

    // Selecting a new date range is always intentional; any prior error is no
    // longer relevant to the incoming request. Clearing it here unmounts
    // PlaceholderErrorModal so the normal loading overlay is shown and
    // the fresh data is rendered once the fetch completes.
    clearError();
    setDatespan({ start: updatedStart, finish: updatedFinish });
    dispatchLoadingAction({ type: PropertyShowLoadingActions.START_REQUEST });
  }, [clearError, timezone, datespan.start, datespan.finish, combinedData]);

  const { address, title } = property;
  const addr = getFullAddressString(address);

  if (!mainData) {
    const pageError = intl.formatMessage({
      id: 'property.property_show.page.error',
      defaultMessage: 'Data unable to be processed.',
    });
    FlashesStore.flash(FlashesStore.ERROR, pageError);
    return null;
  }
  const pageTitle = intl.formatMessage({ id: 'property.property_show.page.title', defaultMessage: 'Enosi - Property: {propertyTitle}' }, { propertyTitle: title });

  // Determines whether to display a message informing the user about the automatic
  // chart grouping selection.
  // Show the grouping message on every hard refresh/page load while data is loading.
  // Once the message starts showing (stage >= RULES and no groupBy from URL), it stays
  // visible until the overlay closes. This prevents the message from disappearing when
  // defaults are applied (which sets groupBy).
  // Skip the message if URL contains groupBy parameter (user's explicit grouping choice).
  const showGroupingMessage = overlayVisible && showGroupingMessageRef.current;
  const { loadingOverlayProps } = buildLoadingOverlayState({
    intl,
    datespan,
    timezone,
    loadingState: loadingReducerState,
    showGroupingMessage,
    onCancelFunc: handleCancel,
  });

  /** Timespan with locale and timezone applied for DashboardControl. */
  const dateSpanWithLocaleAndTimezone = useMemo(() => {
    const { start: startTime, finish: finishTime } = datespan;
    const locale = getDefaultLocale();
    return {
      start: startTime.setLocale(locale).setZone(timezone),
      finish: finishTime.setLocale(locale).setZone(timezone),
    };
  }, [datespan, timezone]);

  /** Date range converted to time range for PropertyShowTradeEnergy. */
  const timeRangeForChart = useMemo(
    () => dateRangeToTimeRange(datespan, timezone),
    [datespan, timezone],
  );

  /**
   * Download handler for DataPack and CSV downloads.
   * For DataPack, propertyUsers is fetched before generating the download.
   */
  const handleDownload = useCallback(
    (type, downloadData, dateRange, intlArg) => {
      downloadManager(type, downloadData, dateRange, intlArg, relayEnvironment, property?.uuid);
    },
    [relayEnvironment, property?.uuid],
  );

  const isCancelledWithoutData = useMemo(
    () => loadingReducerState.state === CANCELLED && !combinedData,
    [loadingReducerState.state, combinedData],
  );
  const noDataMessage = isCancelledWithoutData
    ? intl.formatMessage({ id: 'property.property_show.cancel.chart.no_data.message', defaultMessage: 'Request cancelled. Please select a date and initiate a new request.' })
    : null;
  const skeletonVisible = overlayVisible || (isRecoveringFromCache && !combinedData);

  // When the request is cancelled before any data loads, groupBy is intentionally
  // left unset (so the URL stays clean and the next successful load picks the
  // correct grouping from the real rules count). Use a display-only fallback here
  // so the chart/cards/summary scaffolding still renders with the no-data message
  // instead of showing only the header. This fallback is never written to state
  // or the URL.
  const fallbackGroupBy = useMemo(
    () => (rulesCount > DEFAULT_MAX_VISIBLE_CARDS_COUNT
      ? DATA_GROUP_BY_TRADE_TYPE
      : DATA_GROUP_BY_COUNTERPARTY),
    [rulesCount],
  );
  const displayGroupBy = groupBy || (isCancelledWithoutData ? fallbackGroupBy : undefined);
  const displayChartView = useMemo(
    () => ({
      ...chartView,
      groupBy: displayGroupBy,
      aggregateBy: aggregateBy || DATA_AGGREGATE_BY_PROPERTY,
      xAxisDomain: xAxisDomain || X_AXIS_DOMAIN_FIXED,
    }),
    [chartView, displayGroupBy, aggregateBy, xAxisDomain],
  );

  /** Renders the dashboard control bar with chart controls and download options. */
  const buildPropertyControls = useCallback((propertyShowData, renderError) => (
    <DashboardControl
      allowUnchangedApply={!combinedData}
      chartView={displayChartView}
      chartViewSelector={chartViewSelector}
      controlSetStateFunc={controlSetStateFunc}
      downloadFunc={handleDownload}
      downloadSelectorOpen={downloadSelectorOpen}
      error={renderError}
      isModalOpen={overlayVisible}
      mainData={propertyShowData}
      timespan={dateSpanWithLocaleAndTimezone}
      timespanUpdateFunc={handleTimespanUpdate}
      timezone={timezone}
      setChartViewFunc={setChartView}
      source={source}
      toggleFunc={toggle}
      viewSelectorOpen={viewSelectorOpen}
      unit={unit}
    />
  ), [
    combinedData, controlSetStateFunc, dateSpanWithLocaleAndTimezone, displayChartView,
    downloadSelectorOpen, handleDownload, handleTimespanUpdate, overlayVisible, source,
    timezone, toggle, unit, viewSelectorOpen,
  ]);

  const breadcrumbLabel = intl.formatMessage({ id: 'property.property_show.breadcrumbs.properties.label', defaultMessage: ' Properties' });
  const overviewTitle = intl.formatMessage({ id: 'property.property_show.heading.overview.title', defaultMessage: 'Overview' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>
      <Breadcrumbs breadcrumbs={[{
        name: breadcrumbLabel, path: '/properties',
      }, { name: title }]}
      />
      <Heading
        controls={buildPropertyControls(mainData, error)}
        context={overviewTitle}
        subtitle={addr}
        title={title}
      />
      {error ? (
        <PlaceholderErrorModal
          bodyText={formatAsyncErrorBodyText(intl, messages, queryType)}
          error={error}
          onCancelFunc={handleErrorDismiss}
          onRetryFunc={handleRetry}
          placeholderComponent={Placeholder}
          retryDisabled={isRetrying}
        />
      ) : (
        <>
          {skeletonVisible && (
            <>
              {overlayVisible && <LoadingOverlay inputs={loadingOverlayProps} />}
              <Placeholder />
            </>
          )}
          {displayGroupBy && (
            <div style={skeletonVisible ? { visibility: 'hidden', position: 'absolute' } : {}}>
              <PropertyShowTradeEnergy
                controlSetStateFunc={controlSetStateFunc}
                chartView={displayChartView}
                handleTimespanUpdate={handleTimespanUpdate}
                mainData={mainData}
                historianAggregation={finalHistorianAggregation}
                noDataMessage={noDataMessage}
                onChartReadyFunc={onChartReady}
                source={source}
                tradeAggregation={finalTradeAggregation}
                timespan={timeRangeForChart}
                timezone={timezone}
                unit={unit}
              />
              {address && <Map address={address} />}
            </div>
          )}
        </>
      )}
    </>
  );
}

export default PropertyShow;

PropertyShow.propTypes = {
  match: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }).isRequired,
  queryTimezone: PropTypes.string,
  initialView: PropTypes.shape({
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
    xAxisDomain: PropTypes.string,
  }),
  onDatespanChangeFunc: PropTypes.func,
};
